import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus, FaEdit, FaTrash, FaTrashAlt, FaEye, FaSave, FaSearch, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaShieldAlt, FaFlask, FaLeaf, FaFire,
  FaChartBar, FaArrowLeft, FaFilter, FaInfoCircle, FaDownload,
  FaFileUpload, FaExternalLinkAlt
} from 'react-icons/fa';
import { riskAssessmentAPI, productsAPI, aiAPI, settingsAPI, commercialConfigAPI } from '../services/api';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import { ActionsMenu } from '../components/ActionsMenu';
import VersionHistoryModal from '../components/VersionHistoryModal';
import StatusBadge from '../components/StatusBadge';
import { FaRobot, FaPrint } from 'react-icons/fa';
import { printAssessmentReport, riskAssessmentReportHTML } from '../utils/assessmentReports';
import TablePagination from '../components/TablePagination';
import ApprovalPanel from '../components/ApprovalPanel';
import { RiskControlSection, FmeaWorksheet, buildDefaultFmeaItems, riskControlProblems } from '../components/RiskControlFmea';

const printRiskAssessment = (item) => printAssessmentReport({
  reportKey: 'risk_assessment',
  title: `Risk Assessment - ${item.product_code || item.product_name || item.id}`,
  documentName: 'Cleaning Validation Risk Assessment',
  build: () => riskAssessmentReportHTML(item),
});

const SOLUBILITY_OPTIONS = [
  'Very Soluble', 'Freely Soluble', 'Soluble', 'Sparingly Soluble',
  'Slightly Soluble', 'Very Slightly Soluble', 'Practically Insoluble', 'Insoluble'
];
const CLEANABILITY_OPTIONS = ['Easy', 'Medium', 'Difficult'];
const PATIENT_POPULATION_OPTIONS = ['General', 'Pediatric', 'Immunocompromised', 'Oncology'];

// E-sign / workflow statuses offered by the status filter below.
const WORKFLOW_STATUSES = ['Draft', 'Pending Approval', 'Pending QA Approval', 'Approved', 'Rejected'];

const APPROVAL_DECISIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'return',  label: 'Return to Draft' },
  { value: 'reject',  label: 'Reject' },
];

// Pure scoring helpers ported from backend/services/riskAssessmentEngine.js so the
// deep-link pre-fill (ProductMaster → Risk button) can compute live numeric scores
// instead of leaving them at the EMPTY_FORM default of 1.
const scoreFromToxicity = (pdeValue, ld50Value) => {
  const pde = Number(pdeValue);
  if (pdeValue !== null && pdeValue !== undefined && pdeValue !== '' && !Number.isNaN(pde) && pde > 0) {
    if (pde > 1000) return 1;
    if (pde >= 100) return 2;
    return 3;
  }
  const ld50 = Number(ld50Value);
  if (ld50Value !== null && ld50Value !== undefined && ld50Value !== '' && !Number.isNaN(ld50) && ld50 > 0) {
    if (ld50 > 2000) return 1;
    if (ld50 >= 500) return 2;
    return 3;
  }
  return 2; // No toxicological data on file yet — treat as medium pending review
};

const SOLUBILITY_SCORE_MAP = {
  'very soluble': 1, 'freely soluble': 1,
  'soluble': 2, 'sparingly soluble': 2,
  'slightly soluble': 3, 'very slightly soluble': 3, 'practically insoluble': 3, 'insoluble': 3,
};
const scoreFromSolubility = (classification) => {
  const key = String(classification || '').trim().toLowerCase();
  return SOLUBILITY_SCORE_MAP[key] ?? 2;
};

const CLEANABILITY_SCORE_MAP = { easy: 1, medium: 2, difficult: 3 };
const scoreFromCleanability = (rating) => {
  const key = String(rating || '').trim().toLowerCase();
  return CLEANABILITY_SCORE_MAP[key] ?? 2;
};

const RISK_COLOR_MAP = {
  green:  { bg: 'var(--success-bg)', border: 'var(--success-border)', text: 'var(--success-text)', badge: 'var(--success-bg)' },
  yellow: { bg: 'var(--warning-bg)', border: 'var(--warning-border)', text: 'var(--warning-text)', badge: 'var(--warning-bg)' },
  orange: { bg: 'var(--warning-bg)', border: 'var(--warning-border)', text: 'var(--warning-text)', badge: 'var(--warning-bg)' },
  red:    { bg: 'var(--danger-bg)', border: 'var(--danger-border)', text: 'var(--danger-text)', badge: 'var(--danger-bg)' },
};

const RISK_SCORE_GUIDE = {
  toxicity: [
    { score: 1, label: 'Low', desc: 'ADE/PDE > 1000 µg/day or LD50 > 2000 mg/kg' },
    { score: 2, label: 'Medium', desc: 'ADE/PDE 100–1000 µg/day or LD50 500–2000 mg/kg' },
    { score: 3, label: 'High', desc: 'ADE/PDE < 100 µg/day or LD50 < 500 mg/kg or hazardous classification' },
  ],
  solubility: [
    { score: 1, label: 'Low Risk', desc: 'Very Soluble / Freely Soluble (easy to rinse)' },
    { score: 2, label: 'Medium Risk', desc: 'Soluble / Sparingly Soluble' },
    { score: 3, label: 'High Risk', desc: 'Slightly Soluble / Insoluble (difficult to remove)' },
  ],
  cleanability: [
    { score: 1, label: 'Low Risk', desc: 'Easy — product rinses off without residue' },
    { score: 2, label: 'Medium Risk', desc: 'Medium — requires specific cleaning agent or method' },
    { score: 3, label: 'High Risk', desc: 'Difficult — sticky, waxy, or strongly adherent residues' },
  ],
  patient_population: [
    { score: 1, label: 'General', desc: 'Standard adult patient population' },
    { score: 2, label: 'Pediatric', desc: 'Children — lower body weight, higher sensitivity' },
    { score: 3, label: 'Oncology / Immunocompromised', desc: 'Critically vulnerable patients' },
  ],
  batch_size: [
    { score: 1, label: 'Large Batch', desc: 'Large batch → lower residue concentration risk' },
    { score: 2, label: 'Medium Batch', desc: 'Medium batch size' },
    { score: 3, label: 'Small Batch', desc: 'Small batch → higher residue concentration per dose' },
  ],
  equipment: [
    { score: 1, label: 'Dedicated', desc: 'Dedicated equipment — no cross-contamination risk' },
    { score: 2, label: 'Semi-shared', desc: 'Limited sharing, same product family' },
    { score: 3, label: 'Shared', desc: 'Fully shared multi-product equipment' },
  ],
};

const EMPTY_FORM = {
  product_id: '', product_name: '', product_code: '',
  api_id: '', api_name: '', api_code: '',
  toxicity_risk_score: 1, toxicity_basis: 'ADE/PDE',
  ade_pde_value: '', ade_pde_unit: 'µg/day',
  ld50_value: '', ld50_unit: 'mg/kg',
  is_genotoxic: false, is_carcinogenic: false, is_sensitizer: false,
  toxicity_notes: '',
  solubility_risk_score: 1, solubility_class: '', solubility_notes: '',
  cleanability_risk_score: 1, cleanability_rating: '', cleanability_notes: '',
  patient_population_risk: 1, patient_population: 'General',
  batch_size_risk: 1, batch_size: '', batch_size_unit: 'kg',
  max_daily_dose: '', max_daily_dose_unit: 'mg',
  shared_equipment: true, equipment_risk_score: 3, equipment_notes: '',
  additional_notes: '',
  regulatory_reference: '',
  risk_ranking_guide_section: '',
  current_controls: '', proposed_controls: '', control_evidence_ref: '', control_evidence_files: [],
  fmea_items: [],
  plant_code: '', line_id: '', status: 'Draft',
};

const computeLocalRisk = (form) => {
  const {
    toxicity_risk_score = 1, solubility_risk_score = 1, cleanability_risk_score = 1,
    patient_population_risk = 1, batch_size_risk = 1, equipment_risk_score = 1,
    is_genotoxic = false, is_carcinogenic = false,
  } = form;

  let total = Number(toxicity_risk_score) + Number(solubility_risk_score) + Number(cleanability_risk_score) +
    Number(patient_population_risk) + Number(batch_size_risk) + Number(equipment_risk_score);
  if (is_genotoxic || is_carcinogenic) total += 3;

  if (total >= 15) return { total, level: 'Critical', color: 'red' };
  if (total >= 11) return { total, level: 'High', color: 'orange' };
  if (total >= 7)  return { total, level: 'Medium', color: 'yellow' };
  return { total, level: 'Low', color: 'green' };
};

const RiskBadge = ({ level, color, score }) => {
  const colors = RISK_COLOR_MAP[color] || RISK_COLOR_MAP.green;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '4px 12px', borderRadius: '999px', fontWeight: 700,
      fontSize: 'var(--fs-md)', border: `1.5px solid ${colors.border}`,
      backgroundColor: colors.badge, color: colors.text
    }}>
      {level === 'Critical' && <FaFire />}
      {level === 'High' && <FaExclamationTriangle />}
      {level === 'Medium' && <FaInfoCircle />}
      {level === 'Low' && <FaLeaf />}
      {level} {score !== undefined && `(Score: ${score})`}
    </span>
  );
};

const ScoreSelector = ({ label, guideKey, value, onChange }) => {
  const guide = RISK_SCORE_GUIDE[guideKey] || [];
  const active = guide.find(o => o.score === value);
  return (
    <div style={{ marginBottom: '10px' }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '6px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {guide.map(opt => (
          <button
            key={opt.score}
            type="button"
            onClick={() => onChange(opt.score)}
            title={opt.desc}
            style={{
              flex: '1 1 auto', minWidth: '70px', padding: '5px 8px', borderRadius: '7px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              border: value === opt.score
                ? `2px solid ${opt.score === 1 ? 'var(--success-color)' : opt.score === 2 ? 'var(--warning-color)' : 'var(--danger-color)'}`
                : '1px solid var(--border-color)',
              background: value === opt.score
                ? (opt.score === 1 ? 'var(--success-bg)' : opt.score === 2 ? 'var(--warning-bg)' : 'var(--danger-bg)')
                : 'var(--card-bg)',
              color: value === opt.score
                ? (opt.score === 1 ? 'var(--success-text)' : opt.score === 2 ? 'var(--warning-text)' : 'var(--danger-text)')
                : 'var(--text-secondary)',
              fontWeight: value === opt.score ? 700 : 500, fontSize: 'var(--fs-sm)',
              cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
            }}
          >
            <span style={{ fontSize: 'var(--fs-md)', fontWeight: 800 }}>{opt.score}</span>
            <span>{opt.label}</span>
          </button>
        ))}
      </div>
      {active && (
        <div style={{ marginTop: '4px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          {active.desc}
        </div>
      )}
    </div>
  );
};

const RiskAssessment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'form' | 'detail'
  const initialFormRef = useRef('');
  const [editingId, setEditingId] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [historyModal, setHistoryModal] = useState(null); // risk assessment row
  const ai = useAIAction();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [approvalRequired, setApprovalRequired] = useState(true);

  // E-sign
  const [showESign, setShowESign] = useState(false);
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(null); // {item} while the gate is open for an Approved row
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [eSignMode, setESignMode] = useState('submit');
  const [eSignData, setESignData] = useState({ password: '', comment: '', decision: 'approve' });
  const [eSigning, setESigning] = useState(false);
  const passwordRef = useRef(null);

  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const role = String(user.role || '').trim().toLowerCase();
  const isSuperAdmin = role === 'super admin';
  const isAdmin = role === 'admin' || isSuperAdmin;

  const liveRisk = useMemo(() => computeLocalRisk(form), [form]);
  const itemCanEdit = useCallback((item) => !approvalRequired || !item?.status || item.status === 'Draft', [approvalRequired]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [assessRes, productRes, settRes] = await Promise.all([
        riskAssessmentAPI.getAll(),
        productsAPI.getAll({ limit: 2000 }),
        settingsAPI.getAll(),
      ]);
      setAssessments(assessRes?.data?.data || []);
      const prodRows = productRes?.data?.data || productRes?.data?.products || [];
      setProducts(Array.isArray(prodRows) ? prodRows : []);
      const settings = settRes?.data?.data || {};
      setApprovalRequired(settings['workflow_approval_required_risk_assessment'] !== 'false');
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (showESign) setTimeout(() => passwordRef.current?.focus(), 80); }, [showESign]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Support deep-link from ProductMaster: ?product_id=X&product_name=Y&product_code=Z
  // A risk assessment is now auto-generated the moment a product is added, so this
  // opens the existing assessment for review instead of creating a duplicate blank one.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const pid = params.get('product_id');
    if (pid && view === 'list') {
      const existing = assessments.find(a => String(a.product_id) === String(pid));
      if (existing) {
        if (itemCanEdit(existing)) handleEdit(existing);
        else handleView(existing);
        return;
      }
      const pname = params.get('product_name') || '';
      const pcode = params.get('product_code') || '';
      const api_name = params.get('api_name') || '';
      const ade_pde = params.get('ade_pde_value') || '';
      const solubility = params.get('solubility_class') || '';
      const cleanability = params.get('cleanability_rating') || '';
      setForm(f => ({
        ...EMPTY_FORM,
        product_id: pid,
        product_name: pname,
        product_code: pcode,
        api_name,
        ade_pde_value: ade_pde,
        solubility_class: solubility,
        cleanability_rating: cleanability,
        toxicity_risk_score: scoreFromToxicity(ade_pde, null),
        solubility_risk_score: scoreFromSolubility(solubility),
        cleanability_risk_score: scoreFromCleanability(cleanability),
        plant_code: localStorage.getItem('activePlant') || '',
        line_id: localStorage.getItem('activeLine') || '',
      }));
      setView('form');
      setEditingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, view, loading, assessments]);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleProductSelect = (e) => {
    const pid = e.target.value;
    const prod = products.find(p => String(p.product_id) === String(pid));
    if (prod) {
      setForm(f => ({
        ...f,
        product_id: pid,
        product_name: prod.product_name || '',
        product_code: prod.product_code || '',
      }));
    } else {
      setForm(f => ({ ...f, product_id: pid, product_name: '', product_code: '' }));
    }
  };

  const handleSave = async () => {
    if (!form.product_name) return toast.warning('Product name is required');
    if (editingId && initialFormRef.current === JSON.stringify(form)) {
      return toast.info('No changes were made — nothing to save.');
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        plant_code: form.plant_code || localStorage.getItem('activePlant') || null,
        line_id: form.line_id || localStorage.getItem('activeLine') || null,
      };
      if (editingId) {
        await riskAssessmentAPI.update(editingId, payload);
        toast.success('Risk assessment updated');
      } else {
        await riskAssessmentAPI.create(payload);
        toast.success('Risk assessment created');
      }
      await loadData();
      setView('list');
      setEditingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    if (!itemCanEdit(item)) {
      toast.warn('This assessment is in the approval workflow — return it to Draft before editing.');
      return handleView(item);
    }
    const initial = {
      ...EMPTY_FORM,
      ...item,
      is_genotoxic: item.is_genotoxic || false,
      is_carcinogenic: item.is_carcinogenic || false,
      is_sensitizer: item.is_sensitizer || false,
      shared_equipment: item.shared_equipment !== false,
      control_evidence_files: Array.isArray(item.control_evidence_files) ? item.control_evidence_files : [],
      fmea_items: Array.isArray(item.fmea_items) ? item.fmea_items : [],
    };
    initialFormRef.current = JSON.stringify(initial);
    // Assessments created before the FMEA worksheet start from one row per risk factor.
    setForm(initial.fmea_items.length ? initial : { ...initial, fmea_items: buildDefaultFmeaItems(initial) });
    setEditingId(item.id);
    setView('form');
  };

  const handleView = (item) => {
    setViewingItem(item);
    setView('detail');
  };

  // ── E-sign ─────────────────────────────────────────────────────────────────
  const openESign = (mode, item) => {
    const target = item || viewingItem;
    if (mode === 'returnToDraft' && target?.status === 'Approved' && !pendingChangeRef) {
      setChangeRefGate({ item: target });
      return;
    }
    setESignMode(mode);
    setESignData({ password: '', comment: '', decision: 'approve' });
    if (item) setViewingItem(item);
    setShowESign(true);
  };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      setViewingItem(changeRefGate.item);
      setChangeRefGate(null);
      setESignMode('returnToDraft');
      setESignData({ password: '', comment: '', decision: 'approve' });
      setShowESign(true);
    } finally {
      setChangeRefBusy(false);
    }
  };
  const closeESign = () => { setShowESign(false); setESigning(false); };
  const [approvalLevel, setApprovalLevel] = useState(null);
  const [approvalRefresh, setApprovalRefresh] = useState(0);

  const handleConfirmESign = async () => {
    if (!eSignData.password) return toast.error('Password required');
    if (!eSignData.comment.trim()) return toast.error('Comment required');
    const target = viewingItem;
    if (!target) return;
    setESigning(true);
    try {
      if (eSignMode === 'submit') {
        const res = await riskAssessmentAPI.submit(target.id, { password: eSignData.password, comment: eSignData.comment });
        setViewingItem(res?.data?.data || target);
        toast.success('Submitted for approval');
      } else if (eSignMode === 'returnToDraft') {
        const payload = { password: eSignData.password, comment: eSignData.comment };
        if (target.status === 'Approved' && pendingChangeRef) {
          payload.change_reference_type = pendingChangeRef.reference_type;
          payload.change_reference_number = pendingChangeRef.reference_number;
          payload.change_reference_files = pendingChangeRef.reference_files || [];
        }
        const res = await riskAssessmentAPI.returnToDraft(target.id, payload);
        setViewingItem(res?.data?.data || target);
        setPendingChangeRef(null);
        toast.success('Returned to Draft — you can now edit');
      } else if (eSignMode === 'delete') {
        await riskAssessmentAPI.delete(target.id, { password: eSignData.password, comment: eSignData.comment });
        toast.success('Deactivated');
        setView('list');
        setViewingItem(null);
      } else {
        // 'product' | 'qa'
        const res = await riskAssessmentAPI.approve(target.id, {
          stage: eSignMode, decision: eSignData.decision, comment: eSignData.comment, password: eSignData.password,
        });
        setViewingItem(res?.data?.data || target);
        const label = eSignData.decision === 'approve' ? 'Approved' : eSignData.decision === 'return' ? 'Returned' : 'Rejected';
        toast.success(res?.data?.message || label);
        setApprovalRefresh((n) => n + 1);
      }
      closeESign();
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Action failed');
    } finally {
      setESigning(false);
    }
  };

  const handleDelete = (item) => {
    if (!isAdmin) return toast.error('Only admins can deactivate risk assessments');
    openESign('delete', item);
  };

  const handleNew = () => {
    setForm({
      ...EMPTY_FORM,
      plant_code: localStorage.getItem('activePlant') || '',
      line_id: localStorage.getItem('activeLine') || '',
    });
    setEditingId(null);
    setView('form');
    navigate('/risk-assessment', { replace: true });
  };

  const filteredAssessments = useMemo(() => {
    return assessments.filter(a => {
      const q = searchQuery.toLowerCase();
      const matchQ = !q || (a.product_name || '').toLowerCase().includes(q) ||
        (a.product_code || '').toLowerCase().includes(q) ||
        (a.api_name || '').toLowerCase().includes(q);
      const matchStatus = !filterStatus || a.status === filterStatus;
      const matchRisk = !filterRisk || a.risk_level === filterRisk;
      return matchQ && matchStatus && matchRisk;
    });
  }, [assessments, searchQuery, filterStatus, filterRisk]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const totalPages = Math.max(1, Math.ceil(filteredAssessments.length / itemsPerPage));
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterStatus, filterRisk, itemsPerPage]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);
  const paginatedAssessments = filteredAssessments.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    );
  }

  const eSignTitle = {
    submit:         'Submit for Approval — Electronic Signature',
    product:        'Approval — Electronic Signature',
    qa:             'QA Approval — Electronic Signature',
    level:          `${approvalLevel?.label || 'Approval'} (Level ${approvalLevel?.level || 1}) — Electronic Signature`,
    returnToDraft:  'Return to Draft — Electronic Signature',
    delete:         'Deactivate Record — Electronic Signature',
  }[eSignMode];

  // ── E-Sign Modal (rendered from the list/detail views wherever a workflow
  // action can be triggered — copied near-verbatim from AcceptanceCriteria.js) ──
  const ESignModal = () => !showESign ? null : (
    <div className="modal-overlay" style={{ backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === e.currentTarget) closeESign(); }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ fontSize: 'var(--fs-xl)' }}>{eSignTitle}</h2>
          <button className="modal-close" onClick={closeESign}>×</button>
        </div>
        <div className="modal-body">
          {(eSignMode === 'product' || eSignMode === 'qa' || eSignMode === 'level') && (
            <div className="form-group">
              <label className="form-label">Decision *</label>
              <select className="form-control" value={eSignData.decision}
                onChange={e => setESignData(p => ({ ...p, decision: e.target.value }))}>
                {APPROVAL_DECISIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Login Password *</label>
            <input ref={passwordRef} type="password" className="form-control"
              autoComplete="new-password" data-lpignore="true"
              value={eSignData.password}
              onChange={e => setESignData(p => ({ ...p, password: e.target.value }))}
              placeholder="Enter your login password"
              onKeyDown={e => { if (e.key === 'Enter') handleConfirmESign(); }} />
          </div>
          <div className="form-group">
            <label className="form-label">Reason / Comment *</label>
            <textarea className="form-control" rows={3} value={eSignData.comment}
              onChange={e => setESignData(p => ({ ...p, comment: e.target.value }))}
              placeholder="Provide reason or comment…" />
          </div>
          <div style={{ padding: '8px 12px', background: 'var(--bg-color)', borderRadius: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
            <strong>Signed by:</strong> {user.username || 'Unknown'} &nbsp;|&nbsp;
            <strong>Date/Time:</strong> {new Date().toLocaleString()} &nbsp;|&nbsp;
            <strong>Role:</strong> {user.role}
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
          <button className="btn btn-outline" onClick={closeESign} disabled={eSigning}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirmESign} disabled={eSigning}>
            {eSigning ? 'Processing…' : (
              eSignMode === 'submit'        ? 'Confirm & Submit' :
              eSignMode === 'returnToDraft' ? 'Confirm Return to Draft' :
              eSignMode === 'delete'        ? 'Confirm Deactivate' :
              eSignMode === 'level'         ? `Confirm Level ${approvalLevel?.level || 1} Decision` :
              eSignMode === 'product'       ? 'Confirm Product Decision' :
                                               'Confirm QA Decision'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (view === 'detail' && viewingItem) {
    const colors = RISK_COLOR_MAP[viewingItem.risk_color] || RISK_COLOR_MAP.green;
    const dStatus = viewingItem.status || 'Draft';
    const dCanEdit = itemCanEdit(viewingItem);
    return (
      <div style={{ padding: '0 0 40px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setView('list')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaArrowLeft /> Back
          </button>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Risk Assessment Detail</h2>
          <StatusBadge status={dStatus} />
          <div className="toolbar-full" style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn-sm btn-outline" onClick={() => printRiskAssessment(viewingItem)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaPrint /> Print / PDF
            </button>
            <button
              className="btn btn-sm btn-outline"
              data-module="ai_assistant" onClick={() => ai.run('AI Review — Risk Assessment', () => aiAPI.reviewRisk(viewingItem.id))}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <FaRobot /> AI Review
            </button>
            {viewingItem.product_id && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => navigate('/maco', { state: { nextProductId: viewingItem.product_id } })}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FaExternalLinkAlt /> View in MACO
              </button>
            )}
            {dCanEdit && (
              <button className="btn btn-sm btn-outline" onClick={() => handleEdit(viewingItem)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaEdit /> Edit
              </button>
            )}
            {approvalRequired && isAdmin && dStatus && dStatus !== 'Draft' && (
              <button className="btn btn-sm btn-outline" onClick={() => openESign('returnToDraft', viewingItem)} title="Return to Draft for editing" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaEdit /> Return to Draft
              </button>
            )}
            {isAdmin && (
              <button
                className="btn btn-sm"
                style={{ background: 'none', border: '1px solid var(--danger-border)', color: 'var(--danger-color)', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={() => openESign('delete', viewingItem)}
                title="Deactivate record"
              >
                <FaTrashAlt /> Deactivate
              </button>
            )}
          </div>

          <div className="toolbar-compact" style={{ marginLeft: 'auto', gap: '8px' }}>
            <button
              className="btn btn-sm btn-outline"
              data-module="ai_assistant" onClick={() => ai.run('AI Review — Risk Assessment', () => aiAPI.reviewRisk(viewingItem.id))}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <FaRobot /> AI Review
            </button>
            <ActionsMenu
              title="More actions"
              items={[
                { label: 'View in MACO', icon: <FaExternalLinkAlt />, show: !!viewingItem.product_id, onClick: () => navigate('/maco', { state: { nextProductId: viewingItem.product_id } }) },
                { label: 'Edit', icon: <FaEdit />, show: dCanEdit, onClick: () => handleEdit(viewingItem) },
                { label: 'Version History', icon: <FaEye />, onClick: () => setHistoryModal(viewingItem) },
                { label: 'Return to Draft', icon: <FaEdit />, show: approvalRequired && isAdmin && dStatus && dStatus !== 'Draft', onClick: () => openESign('returnToDraft', viewingItem) },
                { label: 'Deactivate', icon: <FaTrashAlt />, danger: true, show: isAdmin, onClick: () => openESign('delete', viewingItem) },
              ]}
            />
          </div>
        </div>

        {approvalRequired && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {dCanEdit && (
              <button className="btn btn-outline btn-sm" onClick={() => {
                const problems = riskControlProblems(viewingItem);
                if (problems.length) return toast.error(`Complete risk controls before submitting: ${problems[0]}${problems.length > 1 ? ` (+${problems.length - 1} more)` : ''}`);
                openESign('submit', viewingItem);
              }} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaFileUpload /> Submit for Approval
              </button>
            )}
          </div>
        )}
        {approvalRequired && viewingItem?.id && dStatus && dStatus !== 'Draft' && (
          <ApprovalPanel
            workflowType="risk_assessment.approval"
            entityId={viewingItem.id}
            pending={['Pending Approval', 'Pending QA Approval'].includes(dStatus)}
            refreshKey={`${dStatus}-${approvalRefresh}`}
            onSign={(level) => { setApprovalLevel(level); openESign('level', viewingItem); }}
          />
        )}

        {(viewingItem.saved_by_name || viewingItem.product_approved_by_name || viewingItem.qa_approved_by_name) && (
          <div style={{ padding: '8px 14px', border: '1px solid var(--border-color)', borderRadius: '8px', display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            {viewingItem.saved_by_name && <span><strong>Saved:</strong> {viewingItem.saved_by_name} on {viewingItem.saved_at ? new Date(viewingItem.saved_at).toLocaleString() : '—'}</span>}
            {viewingItem.product_approved_by_name && <span><strong>Product:</strong> {viewingItem.product_approval_decision} by {viewingItem.product_approved_by_name} on {viewingItem.product_approved_at ? new Date(viewingItem.product_approved_at).toLocaleString() : '—'}</span>}
            {viewingItem.qa_approved_by_name && <span><strong>QA:</strong> {viewingItem.qa_approval_decision} by {viewingItem.qa_approved_by_name} on {viewingItem.qa_approved_at ? new Date(viewingItem.qa_approved_at).toLocaleString() : '—'}</span>}
          </div>
        )}

        <AIResultPanel
          isOpen={ai.isOpen}
          onClose={ai.close}
          title={ai.title}
          loading={ai.loading}
          error={ai.error}
          content={ai.content}
          exportFileName={`risk-assessment-${viewingItem.id}-ai-review.pdf`}
        />

        {/* Risk Summary Banner */}
        <div style={{ background: colors.bg, border: `2px solid ${colors.border}`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Overall Risk Level</div>
              <RiskBadge level={viewingItem.risk_level} color={viewingItem.risk_color} score={viewingItem.total_risk_score} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Product</div>
              <div style={{ fontWeight: 700, fontSize: 'var(--fs-xl)' }}>{viewingItem.product_code} — {viewingItem.product_name}</div>
              {viewingItem.api_name && <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>API: {viewingItem.api_name}</div>}
            </div>
          </div>
        </div>

        {Number.isFinite(Number(viewingItem.rpn_score)) && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', marginBottom: '10px' }}>FMEA Risk Priority (Severity × Occurrence × Detectability)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '10px' }}>
              {[
                { label: 'Severity', score: viewingItem.severity_score, detail: viewingItem.fmea_breakdown?.severity?.source },
                { label: 'Occurrence', score: viewingItem.occurrence_score, detail: viewingItem.fmea_breakdown?.occurrence?.source },
                { label: 'Detectability', score: viewingItem.detectability_score, detail: viewingItem.fmea_breakdown?.detectability?.source },
              ].map(({ label, score, detail }) => (
                <div key={label} style={{ border: '1px solid var(--border-light, #eee)', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 700 }}>{label}</span>
                    <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--input-bg, #f1f5f9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 'var(--fs-sm)' }}>{score ?? '—'}</span>
                  </div>
                  {detail && <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{detail}</div>}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700 }}>
              RPN = {viewingItem.severity_score ?? '—'} × {viewingItem.occurrence_score ?? '—'} × {viewingItem.detectability_score ?? '—'} = {viewingItem.rpn_score}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Toxicological Risk', score: viewingItem.toxicity_risk_score, notes: viewingItem.toxicity_notes, extra: `Basis: ${viewingItem.toxicity_basis || '—'} | ADE/PDE: ${viewingItem.ade_pde_value || '—'} ${viewingItem.ade_pde_unit || ''} | LD50: ${viewingItem.ld50_value || '—'} ${viewingItem.ld50_unit || ''}` },
            { label: 'Solubility Risk', score: viewingItem.solubility_risk_score, notes: viewingItem.solubility_notes, extra: viewingItem.solubility_class || '—' },
            { label: 'Cleanability Risk', score: viewingItem.cleanability_risk_score, notes: viewingItem.cleanability_notes, extra: viewingItem.cleanability_rating || '—' },
            { label: 'Patient Population Risk', score: viewingItem.patient_population_risk, notes: null, extra: viewingItem.patient_population || '—' },
            { label: 'Batch Size Risk', score: viewingItem.batch_size_risk, notes: null, extra: viewingItem.batch_size ? `${viewingItem.batch_size} ${viewingItem.batch_size_unit || ''}` : '—' },
            { label: 'Equipment Risk', score: viewingItem.equipment_risk_score, notes: viewingItem.equipment_notes, extra: viewingItem.shared_equipment ? 'Shared Equipment' : 'Dedicated Equipment' },
          ].map(({ label, score, notes, extra }) => {
            const s = Number(score) || 1;
            const sc = s === 1 ? 'green' : s === 2 ? 'yellow' : 'orange';
            const c = RISK_COLOR_MAP[sc];
            return (
              <div key={label} style={{ background: 'var(--card-bg)', border: `1px solid ${c.border}`, borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--fs-md)', marginBottom: '8px', color: 'var(--text-primary)' }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: c.badge, border: `2px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 'var(--fs-md)', color: c.text }}>{s}</span>
                  <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>{extra}</span>
                </div>
                {notes && <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{notes}</div>}
              </div>
            );
          })}
        </div>

        {dCanEdit && approvalRequired && (() => {
          const problems = riskControlProblems(viewingItem);
          return problems.length > 0 && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: 'var(--fs-md)', color: 'var(--warning-text)' }}>
              <div style={{ fontWeight: 700, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}><FaExclamationTriangle /> Needed before Submit for Approval</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          );
        })()}

        <RiskControlSection readOnly value={viewingItem} riskLevel={viewingItem.risk_level} />
        <FmeaWorksheet readOnly items={Array.isArray(viewingItem.fmea_items) ? viewingItem.fmea_items : []} />

        {/* Special Flags */}
        {(viewingItem.is_genotoxic || viewingItem.is_carcinogenic || viewingItem.is_sensitizer) && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
            <div style={{ fontWeight: 700, color: 'var(--danger-text)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}><FaExclamationTriangle /> Special Hazard Flags</div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {viewingItem.is_genotoxic && <span style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '3px 10px', borderRadius: '999px', fontSize: 'var(--fs-md)', fontWeight: 600 }}>Genotoxic (+3 score)</span>}
              {viewingItem.is_carcinogenic && <span style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '3px 10px', borderRadius: '999px', fontSize: 'var(--fs-md)', fontWeight: 600 }}>Carcinogenic (+3 score)</span>}
              {viewingItem.is_sensitizer && <span style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)', padding: '3px 10px', borderRadius: '999px', fontSize: 'var(--fs-md)', fontWeight: 600 }}>Sensitizer</span>}
            </div>
          </div>
        )}

        {/* Risk Ranking Recommendations */}
        {[
          { title: 'Cleaning Strategy', content: viewingItem.cleaning_strategy, icon: <FaShieldAlt /> },
          { title: 'Validation Approach', content: viewingItem.validation_approach, icon: <FaCheckCircle /> },
          { title: 'Special Precautions', content: viewingItem.special_precautions, icon: <FaExclamationTriangle /> },
        ].map(({ title, content, icon }) => content && (
          <div key={title} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', marginBottom: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {icon} {title}
            </div>
            <p style={{ margin: 0, fontSize: 'var(--fs-base)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{content}</p>
          </div>
        ))}

        {viewingItem.additional_notes && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', marginBottom: '8px' }}>Additional Notes</div>
            <p style={{ margin: 0, fontSize: 'var(--fs-base)', color: 'var(--text-secondary)' }}>{viewingItem.additional_notes}</p>
          </div>
        )}

        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '16px' }}>
          Regulatory Reference: {viewingItem.regulatory_reference || 'Not specified'} {viewingItem.risk_ranking_guide_section ? `| Section: ${viewingItem.risk_ranking_guide_section}` : ''}
          {' | '}Status: <strong>{dStatus}</strong>
        </div>

        <ChangeReferenceModal
          open={!!changeRefGate}
          qmsLinked={qmsLinked}
          busy={changeRefBusy}
          recordLabel={changeRefGate?.item?.product_name}
          onCancel={() => setChangeRefGate(null)}
          onConfirm={confirmChangeReference}
        />
        <ESignModal />
      </div>
    );
  }

  // ── FORM VIEW ────────────────────────────────────────────────────────────────
  if (view === 'form') {
    const colors = RISK_COLOR_MAP[liveRisk.color];
    return (
      <div style={{ paddingBottom: '60px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => { setView('list'); setEditingId(null); navigate('/risk-assessment', { replace: true }); }} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaArrowLeft /> Back
          </button>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>{editingId ? 'Edit' : 'New'} Risk Assessment — Cleaning Validation Risk Ranking</h2>
          {editingId && <StatusBadge status={form.status || 'Draft'} />}
          {/* Live Risk Badge */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Live Risk:</span>
            <RiskBadge level={liveRisk.level} color={liveRisk.color} score={liveRisk.total} />
          </div>
        </div>

        {(form.additional_notes || '').startsWith('Auto-generated by PrimeClean') && form.status === 'Draft' && (
          <div style={{ background: 'var(--info-bg)', border: '1.5px solid var(--info-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <FaInfoCircle style={{ color: 'var(--info-text)', marginTop: '2px', flexShrink: 0 }} />
            <div style={{ fontSize: 'var(--fs-md)', color: 'var(--info-text)' }}>
              <strong>Auto-generated on product creation.</strong> Toxicological, solubility and cleanability scores were pulled from the linked API. Please confirm the Patient Population, Batch Size and Equipment Sharing risk factors below, then save to complete the risk ranking assessment.
            </div>
          </div>
        )}

        {/* Risk Meter */}
        <div style={{ background: colors.bg, border: `2px solid ${colors.border}`, borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 600 }}>TOTAL RISK SCORE (max 21 without hazard flags, 24 with)</div>
            <div style={{ display: 'flex', gap: '4px', height: '12px', borderRadius: '6px', overflow: 'hidden', background: 'var(--border-color)' }}>
              {[...Array(21)].map((_, i) => (
                <div key={i} style={{ flex: 1, background: i < liveRisk.total ? (liveRisk.color === 'green' ? '#10b981' : liveRisk.color === 'yellow' ? '#f59e0b' : liveRisk.color === 'orange' ? '#f97316' : '#ef4444') : 'transparent', borderRadius: i === 0 ? '6px 0 0 6px' : i === 20 ? '0 6px 6px 0' : 0, transition: 'background 0.2s' }} />
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--fs-3xl)', fontWeight: 800, color: colors.text }}>{liveRisk.total}</div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>/ 21+</div>
          </div>
        </div>

        {/* Section: Product Info */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFlask /> Product Information
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Link Product</label>
              <select className="form-control form-control-sm" value={form.product_id} onChange={handleProductSelect}>
                <option value="">— Select product —</option>
                {products.map(p => (
                  <option key={p.product_id} value={p.product_id}>{p.product_code} — {p.product_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Product Name *</label>
              <input className="form-control form-control-sm" value={form.product_name} onChange={e => setField('product_name', e.target.value)} placeholder="Product name" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Product Code</label>
              <input className="form-control form-control-sm" value={form.product_code} onChange={e => setField('product_code', e.target.value)} placeholder="Product code" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>API Name</label>
              <input className="form-control form-control-sm" value={form.api_name} onChange={e => setField('api_name', e.target.value)} placeholder="Active pharmaceutical ingredient" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Max Daily Dose</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" value={form.max_daily_dose} onChange={e => setField('max_daily_dose', e.target.value)} placeholder="Value" style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.max_daily_dose_unit} onChange={e => setField('max_daily_dose_unit', e.target.value)} style={{ flex: 1 }}>
                  {['mg', 'g', 'µg', 'mL', 'IU'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Batch Size</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" value={form.batch_size} onChange={e => setField('batch_size', e.target.value)} placeholder="Value" style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.batch_size_unit} onChange={e => setField('batch_size_unit', e.target.value)} style={{ flex: 1 }}>
                  {['kg', 'g', 'L', 'mL', 'Units'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Risk Scoring */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 18px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaChartBar /> Risk Scoring Matrix
            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '8px' }}>1 = Low Risk · 2 = Medium Risk · 3 = High Risk</span>
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '16px 24px' }}>

          {/* Toxicological */}
          <div style={{ borderLeft: '3px solid #ef4444', paddingLeft: '14px', gridColumn: '1 / -1' }}>
            <ScoreSelector label="1. Toxicological Risk Score" guideKey="toxicity" value={Number(form.toxicity_risk_score)} onChange={v => setField('toxicity_risk_score', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Toxicity Basis</label>
                <select className="form-control form-control-sm" value={form.toxicity_basis} onChange={e => setField('toxicity_basis', e.target.value)}>
                  {['ADE/PDE', 'LD50', 'NOEL', 'Expert Judgment', 'Literature Value'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>ADE/PDE Value</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input className="form-control form-control-sm" type="number" value={form.ade_pde_value} onChange={e => setField('ade_pde_value', e.target.value)} placeholder="Value" style={{ flex: 2 }} />
                  <select className="form-control form-control-sm" value={form.ade_pde_unit} onChange={e => setField('ade_pde_unit', e.target.value)} style={{ flex: 1 }}>
                    {['µg/day', 'mg/day', 'ng/day'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>LD50 Value</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input className="form-control form-control-sm" type="number" value={form.ld50_value} onChange={e => setField('ld50_value', e.target.value)} placeholder="Value" style={{ flex: 2 }} />
                  <select className="form-control form-control-sm" value={form.ld50_unit} onChange={e => setField('ld50_unit', e.target.value)} style={{ flex: 1 }}>
                    {['mg/kg', 'g/kg', 'µg/kg'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' }}>
              {[
                { key: 'is_genotoxic', label: 'Genotoxic (+3 risk score)' },
                { key: 'is_carcinogenic', label: 'Carcinogenic (+3 risk score)' },
                { key: 'is_sensitizer', label: 'Sensitizer / Allergen' },
              ].map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: 'var(--fs-md)', color: form[key] ? 'var(--danger-text)' : 'var(--text-secondary)', fontWeight: form[key] ? 700 : 400 }}>
                  <input type="checkbox" checked={form[key]} onChange={e => setField(key, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ marginTop: '10px' }}>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Toxicology Notes</label>
              <textarea className="form-control form-control-sm" rows={2} value={form.toxicity_notes} onChange={e => setField('toxicity_notes', e.target.value)} placeholder="Additional toxicological information..." />
            </div>
          </div>

          {/* Solubility */}
          <div style={{ borderLeft: '3px solid #3b82f6', paddingLeft: '14px' }}>
            <ScoreSelector label="2. Solubility Risk Score" guideKey="solubility" value={Number(form.solubility_risk_score)} onChange={v => setField('solubility_risk_score', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Solubility Classification</label>
                <select className="form-control form-control-sm" value={form.solubility_class} onChange={e => setField('solubility_class', e.target.value)}>
                  <option value="">— Select —</option>
                  {SOLUBILITY_OPTIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Solubility Notes</label>
                <input className="form-control form-control-sm" value={form.solubility_notes} onChange={e => setField('solubility_notes', e.target.value)} placeholder="e.g. soluble in ethanol, insoluble in water" />
              </div>
            </div>
          </div>

          {/* Cleanability */}
          <div style={{ borderLeft: '3px solid #8b5cf6', paddingLeft: '14px' }}>
            <ScoreSelector label="3. Cleanability Risk Score" guideKey="cleanability" value={Number(form.cleanability_risk_score)} onChange={v => setField('cleanability_risk_score', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Cleanability Rating</label>
                <select className="form-control form-control-sm" value={form.cleanability_rating} onChange={e => setField('cleanability_rating', e.target.value)}>
                  <option value="">— Select —</option>
                  {CLEANABILITY_OPTIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Cleanability Notes</label>
                <input className="form-control form-control-sm" value={form.cleanability_notes} onChange={e => setField('cleanability_notes', e.target.value)} placeholder="e.g. sticky residue, forms films" />
              </div>
            </div>
          </div>

          {/* Patient Population */}
          <div style={{ borderLeft: '3px solid #f59e0b', paddingLeft: '14px' }}>
            <ScoreSelector label="4. Patient Population Risk" guideKey="patient_population" value={Number(form.patient_population_risk)} onChange={v => setField('patient_population_risk', v)} />
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Patient Population</label>
              <select className="form-control form-control-sm" value={form.patient_population} onChange={e => setField('patient_population', e.target.value)} style={{ maxWidth: '300px' }}>
                {PATIENT_POPULATION_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Batch Size */}
          <div style={{ borderLeft: '3px solid #10b981', paddingLeft: '14px' }}>
            <ScoreSelector label="5. Batch Size Risk" guideKey="batch_size" value={Number(form.batch_size_risk)} onChange={v => setField('batch_size_risk', v)} />
          </div>

          {/* Equipment */}
          <div style={{ borderLeft: '3px solid #6366f1', paddingLeft: '14px', gridColumn: '1 / -1' }}>
            <ScoreSelector label="6. Equipment Sharing Risk" guideKey="equipment" value={Number(form.equipment_risk_score)} onChange={v => setField('equipment_risk_score', v)} />
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: 'var(--fs-md)' }}>
                <input type="checkbox" checked={form.shared_equipment} onChange={e => setField('shared_equipment', e.target.checked)} />
                Shared / Multi-product equipment
              </label>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input className="form-control form-control-sm" value={form.equipment_notes} onChange={e => setField('equipment_notes', e.target.value)} placeholder="Equipment notes, surface area, etc." />
              </div>
            </div>
          </div>

          </div>
        </div>

        <RiskControlSection value={form} riskLevel={form.risk_level || liveRisk.level}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
        <FmeaWorksheet items={form.fmea_items || []} seedFrom={form} onChange={(items) => setField('fmea_items', items)} />

        {/* Additional Info */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 14px 0', fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Additional Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Regulatory Reference</label>
              <input className="form-control form-control-sm" value={form.regulatory_reference} onChange={e => setField('regulatory_reference', e.target.value)} placeholder="e.g. ISPE Baseline Guide Vol 7, APIC, EMA guideline" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Guide Section / Reference Clause</label>
              <input className="form-control form-control-sm" value={form.risk_ranking_guide_section} onChange={e => setField('risk_ranking_guide_section', e.target.value)} placeholder="e.g. Section 5.3" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Status</label>
              <div style={{ paddingTop: '4px' }}><StatusBadge status={form.status || 'Draft'} /></div>
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Additional Notes / Justification</label>
            <textarea className="form-control form-control-sm" rows={3} value={form.additional_notes} onChange={e => setField('additional_notes', e.target.value)} placeholder="Any additional comments, references to toxicological reports, or justification for risk scores..." />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={() => { setView('list'); navigate('/risk-assessment', { replace: true }); }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaSave /> {saving ? 'Saving...' : (editingId ? 'Update Assessment' : 'Save Assessment')}
          </button>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  const riskCounts = { Low: 0, Medium: 0, High: 0, Critical: 0 };
  assessments.forEach(a => { if (riskCounts[a.risk_level] !== undefined) riskCounts[a.risk_level]++; });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title">Risk Assessment — Cleaning Validation</h2>
          <p className="page-subtitle">Cleaning Validation Risk Ranking | Product introduction risk screening</p>
        </div>
        <button className="btn btn-primary" onClick={handleNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaPlus /> New Assessment
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { level: 'Low', color: 'green', icon: <FaLeaf /> },
          { level: 'Medium', color: 'yellow', icon: <FaInfoCircle /> },
          { level: 'High', color: 'orange', icon: <FaExclamationTriangle /> },
          { level: 'Critical', color: 'red', icon: <FaFire /> },
        ].map(({ level, color, icon }) => {
          const c = RISK_COLOR_MAP[color];
          return (
            <div key={level} style={{ background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: '10px', padding: '14px', textAlign: 'center', cursor: 'pointer', boxShadow: filterRisk === level ? `0 0 0 2px ${c.text}` : 'none', opacity: filterRisk && filterRisk !== level ? 0.6 : 1, transition: 'box-shadow 0.15s, opacity 0.15s' }}
              title={filterRisk === level ? 'Click to clear filter' : `Filter: ${level} risk`}
              onClick={() => setFilterRisk(filterRisk === level ? '' : level)}>
              <div style={{ fontSize: 'var(--fs-2xl)', color: c.text, marginBottom: '4px' }}>{icon}</div>
              <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 800, color: c.text }}>{riskCounts[level]}</div>
              <div style={{ fontSize: 'var(--fs-sm)', color: c.text, fontWeight: 600 }}>{level} Risk</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
          <input className="form-control form-control-sm" style={{ paddingLeft: '30px' }} placeholder="Search product, API..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <select className="form-control form-control-sm" style={{ flex: '0 0 150px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {WORKFLOW_STATUSES.map(o => <option key={o}>{o}</option>)}
        </select>
        <select className="form-control form-control-sm" style={{ flex: '0 0 130px' }} value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
          <option value="">All Risk Levels</option>
          {['Low', 'Medium', 'High', 'Critical'].map(o => <option key={o}>{o}</option>)}
        </select>
        {(filterStatus || filterRisk || searchQuery) && (
          <button className="btn btn-sm btn-outline" onClick={() => { setSearchQuery(''); setFilterStatus(''); setFilterRisk(''); }}>
            Clear Filters
          </button>
        )}
      </div>

      {filteredAssessments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <FaShieldAlt style={{ fontSize: 'var(--fs-3xl)', marginBottom: '12px', opacity: 0.4 }} />
          <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, marginBottom: '8px' }}>No risk assessments found</div>
          <div style={{ fontSize: 'var(--fs-md)', marginBottom: '20px' }}>
            {assessments.length === 0
              ? 'Create a risk assessment when introducing a new product, per your cleaning validation risk ranking guidelines.'
              : 'No results match your filters.'}
          </div>
          {assessments.length === 0 && (
            <button className="btn btn-primary" onClick={handleNew} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <FaPlus /> Create First Assessment
            </button>
          )}
        </div>
      ) : (
        <FloatingScrollWrapper>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-md)' }}>
            <thead>
              <tr style={{ background: 'var(--bg-color)', borderBottom: '2px solid var(--border-color)' }}>
                {['Product Code', 'Product Name', 'API', 'Risk Level', 'Score', 'Status', 'Last Updated', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedAssessments.map(item => {
                const colors = RISK_COLOR_MAP[item.risk_color] || RISK_COLOR_MAP.green;
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-color)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--primary-color)' }}>{item.product_code || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{item.product_name || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{item.api_name || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-sm)', background: colors.badge, color: colors.text, border: `1px solid ${colors.border}` }}>
                        {item.risk_level}
                      </span>
                      {(item.is_genotoxic || item.is_carcinogenic) && (
                        <span title="Genotoxic/Carcinogenic" style={{ marginLeft: '4px', color: 'var(--danger-text)', fontSize: 'var(--fs-md)' }}><FaExclamationTriangle /></span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: colors.text }}>{item.total_risk_score}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <StatusBadge status={item.status || 'Draft'} />
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="action-cell" style={{ width: '120px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button className="btn btn-sm btn-outline" onClick={() => handleView(item)} title="View">
                          <FaEye />
                        </button>
                        <ActionsMenu items={[
                          { label: 'Edit', icon: <FaEdit />, onClick: () => handleEdit(item), show: itemCanEdit(item) },
                          { label: 'Print / PDF', icon: <FaPrint />, onClick: () => printRiskAssessment(item) },
                          { label: 'Version History', icon: <FaEye />, onClick: () => setHistoryModal(item) },
                          { label: 'Deactivate', icon: <FaTrash />, onClick: () => handleDelete(item), show: isAdmin, danger: true },
                        ]} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FloatingScrollWrapper>
      )}

      {!loading && filteredAssessments.length > 0 && (
        <TablePagination total={filteredAssessments.length} page={currentPage} pageSize={itemsPerPage} onPageChange={setCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }} itemLabel="assessments" />
      )}

      <ESignModal />
      <VersionHistoryModal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={historyModal ? `Version History — ${historyModal.product_name || 'Risk Assessment'}` : ''}
        fetcher={() => riskAssessmentAPI.getRevisions(historyModal.id)}
      />
    </div>
  );
};

export default RiskAssessment;
